/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Spin } from 'antd'

import colors from '@sportninja/common/constants/appColors'

import { font, weightSemiBold } from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import type { GameListCounts } from '../hooks/useGameListItems'
import { gameViewsLabel } from '../utils/i18n'

export interface LoadMoreFooterProps {
  counts: GameListCounts
  isFetchingNextPage: boolean
  isLoading: boolean
  visibleCount: number
  /**
   * Views that cannot reach the end by scrolling say so and offer to finish the
   * job. The List view leaves this off: scrolling genuinely gets there, and a
   * running counter over a padded superset is a number nobody can reconcile
   * with what is on screen.
   */
  showIncomplete?: boolean
  isError?: boolean
  errorMessage?: string
  onRetry?: () => void
  isLoadingAll?: boolean
  onLoadAll?: () => void
}

/**
 * End-of-list status.
 *
 * Quiet by default — "fetching" while it fetches, "that's everything" when it
 * stops, and only when there was ever more than one page. The loud states are
 * opt-in because they are only honest for views that cannot page themselves.
 */
const LoadMoreFooter = ({
  counts,
  isFetchingNextPage,
  isLoading,
  visibleCount,
  showIncomplete = false,
  isError = false,
  errorMessage,
  onRetry,
  isLoadingAll = false,
  onLoadAll,
}: LoadMoreFooterProps) => {
  'use memo'

  /*
   * Any page currently in flight — one pulled by scrolling, or the whole
   * remainder pulled by "Load all".
   *
   * They are separate props because they are separate actions, but to a reader
   * they are the same fact: games are arriving. The footer should be moving.
   */
  const isBusy = isFetchingNextPage || isLoadingAll

  const showNoMore =
    !counts.hasUnloadedRawPages &&
    !isLoading &&
    visibleCount > 0 &&
    counts.rawTotalPages > 1

  /*
   * A failed page is the dangerous case: without this the view looks settled
   * and complete while games are missing, and an admin can select-all and bulk
   * edit a set that silently excludes them.
   */
  if (isError) {
    return (
      <div css={noticeStyles(true)} role='alert' data-testid='game-load-error'>
        <span css={noticeTextStyles}>
          {gameViewsLabel('gameLoadFailed')}
          {errorMessage ? ` — ${errorMessage}` : ''}
        </span>
        {onRetry !== undefined && (
          <button type='button' css={actionStyles} onClick={onRetry}>
            {gameViewsLabel('gameLoadRetry')}
          </button>
        )}
      </div>
    )
  }

  /*
   * This branch RETURNS, so the spinner further down never rendered while the
   * incomplete notice was up — which is every moment the Details view is paging
   * in the background. Fetching looked identical to idle: a static count and a
   * button, with nothing to say whether anything was happening.
   */
  if (showIncomplete && counts.hasUnloadedRawPages && !isLoading) {
    return (
      <div
        css={noticeStyles(false)}
        role='status'
        aria-busy={isBusy}
        data-testid='game-load-incomplete'
      >
        {isBusy ? (
          <span css={busyStyles} data-testid='game-load-busy'>
            <Spin size='small' />
            <span css={noticeTextStyles}>
              {gameViewsLabel(
                isLoadingAll ? 'gameLoadingAll' : 'gameLoadingMore'
              )}
            </span>
          </span>
        ) : (
          <span css={noticeTextStyles}>
            {gameViewsLabel('gameLoadIncomplete', {
              matching: counts.matchingLoaded,
            })}
          </span>
        )}
        {onLoadAll !== undefined && (
          <button
            type='button'
            css={actionStyles}
            onClick={onLoadAll}
            //  Disabled while ANY page is in flight, not just "Load all": a
            //  second request stacked on a background page double-counts the
            //  remaining pages and the button never settles.
            disabled={isBusy}
            data-testid='game-load-all'
          >
            {isLoadingAll
              ? gameViewsLabel('gameLoadingAll')
              : gameViewsLabel('gameLoadAll')}
          </button>
        )}
      </div>
    )
  }

  return (
    <div css={footerStyles}>
      {isFetchingNextPage && (
        <div css={loadingStyles}>
          <Spin size='small' />
          <Text
            variant='paragraphSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
            style='font-size: 14px; line-height: 1;'
          >
            {gameViewsLabel('gameLoadingMore')}
          </Text>
        </div>
      )}
      {showNoMore && (
        <Text
          variant='paragraphSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style='font-size: 14px;'
        >
          {gameViewsLabel('gameNoMoreResults')}
        </Text>
      )}
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

const footerStyles = css`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 24px 0;
  min-height: 60px;
`

const busyStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
`

const loadingStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`

const noticeStyles = (isError: boolean) => css`
  ${font.body}
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${isError ? colors.ERROR_200 : colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
`

const noticeTextStyles = css`
  flex: 1 1 auto;
  color: ${colors.NEUTRAL_50};
`

const actionStyles = css`
  ${font.body}
  ${weightSemiBold}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${colors.SECONDARY_300};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

export default LoadMoreFooter
